import {
  dayjsFormatLongOrdinal,
  convert24HourTo12Hour,
  BookingGateWayModeIdEnum,
  generateUserName,
} from '@alluradirect/react';
import { Typography } from '@mui/material';
import Grid from 'components/core/Grid';
import LoadingSkeleton from 'components/core/LoadingSkeleton/LoadingSkeleton';
import { CheckoutSectionTitle } from 'components/views/pages/Checkout/components/CheckoutSectionTitle';
import { generateGuestsDisplay, generatePetsDisplay } from 'components/views/pages/Checkout/helpers';
import dayjs from 'dayjs';
import { CheckoutStepProps } from 'pages/[propertyId]/reservenow';
import { useMemo } from 'react';

export const CheckoutReservationDetails = ({
  bookingData,
  userData,
  adults,
  pChildren,
  infants,
  pets,
}: Pick<CheckoutStepProps, 'bookingData' | 'userData' | 'adults' | 'pChildren' | 'infants' | 'pets'>) => {
  const isLoading = !bookingData;

  const isGatewayClosed = bookingData?.PROPERTY.GATEWAY_MODE.ID === BookingGateWayModeIdEnum.closed;
  const bookingId = bookingData?.REFERENCE ?? '--';
  const arrivalDate = bookingData ? dayjsFormatLongOrdinal(dayjs(bookingData.DATES.ARRIVAL)) : '--';
  const departureDate = bookingData ? dayjsFormatLongOrdinal(dayjs(bookingData.DATES.DEPARTURE)) : '--';
  const checkIn = bookingData ? convert24HourTo12Hour(bookingData.PROPERTY.RULES.CHECK_IN) : '--';
  const checkOut = bookingData ? convert24HourTo12Hour(bookingData.PROPERTY.RULES.CHECK_OUT) : '--';
  const guestName = userData ? generateUserName(userData) : '--';

  const allowsPets = ((bookingData?.PROPERTY as unknown) as any)?.SUITABILITIES?.PETS?.IS_SUITABLE ?? false;

  const sectionTitle = useMemo(() => {
    let title = 'Reservation Details';
    if (isGatewayClosed) {
      title = title + ' (Awaiting Host Approval)';
    }
    return title;
  }, [isGatewayClosed]);

  const guestsDisplay = generateGuestsDisplay({
    adults,
    children: pChildren,
    infants,
  });
  const petsDisplay = generatePetsDisplay({
    pets,
    allowsPets,
  });

  return (
    <Grid item xs={12}>
      <CheckoutSectionTitle>{sectionTitle}</CheckoutSectionTitle>

      <Typography variant="body1">
        <strong>Booking ID:</strong>
      </Typography>
      <LoadingSkeleton isLoading={isLoading}>
        <Typography mb={1} variant="body1">
          #{bookingId}
        </Typography>
      </LoadingSkeleton>

      <Typography variant="body1">
        <strong>Primary Guest</strong>
      </Typography>
      <LoadingSkeleton isLoading={isLoading}>
        <Typography mb={1} variant="body1">
          {guestName}
        </Typography>
      </LoadingSkeleton>

      <Typography variant="body1">
        <strong>Arrival Date:</strong>
      </Typography>
      <LoadingSkeleton isLoading={isLoading}>
        <Typography mb={1} variant="body1">
          {arrivalDate} ({checkIn})
        </Typography>
      </LoadingSkeleton>

      <Typography variant="body1">
        <strong>Departure Date:</strong>
      </Typography>
      <LoadingSkeleton isLoading={isLoading}>
        <Typography variant="body1" mb={1}>
          {departureDate} ({checkOut})
        </Typography>
      </LoadingSkeleton>

      <Typography variant="body1">
        <strong>Guests:</strong>
      </Typography>
      <LoadingSkeleton isLoading={isLoading}>
        <Typography variant="body1" mb={1}>
          {guestsDisplay}
        </Typography>
      </LoadingSkeleton>

      <Typography variant="body1">
        <strong>Pets:</strong>
      </Typography>
      <LoadingSkeleton isLoading={isLoading}>
        <Typography variant="body1" mb={1}>
          {petsDisplay}
        </Typography>
      </LoadingSkeleton>
    </Grid>
  );
};
